import uuid from 'uuid/v4';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage, calculateCurrentPage } from 'store/utils/pagination';
import {
  calculateFinalParams,
  calculateNewFilterOption,
  mergeRulesWithGroups,
  dropItemFromList,
} from 'store/utils/handleParams';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';
import _join from 'lodash/join';
import _isNull from 'lodash/isNull';

import { DESELECT_RULE } from 'store/services/rules/actionTypes';
import {
  selectRule,
  getRule as getCurrentRule,
} from 'store/services/rules/actions';
import { getRule } from 'store/services/rules/selectors';
import { getMatchedRulesMapIds } from 'store/services/annotations/selectors';
/* eslint-disable no-unused-vars */
import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

/**
 * Get all rule groups
 *
 * @param params
 */
export const getAllRuleGroups = (params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_RULE_GROUPS_ALL_REQUEST,
    });
    const { data } = await api.getRuleGroupsListRequest(params);
    dispatch({
      type: types.GET_RULE_GROUPS_ALL_SUCCESS,
      payload: {
        items: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_RULE_GROUPS_ALL_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Get rule groups
 *
 * @param paginationParams
 * @param filterParams
 */
export const getRuleGroupsList = (
  paginationParams = {},
  filterParams = {},
  fetchAll = false,
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.GET_RULE_GROUPS_LIST_REQUEST,
    });
    const state = getState();
    let filterOpt = filterParams;
    if (_isEmpty(filterOpt)) {
      filterOpt = selectors.getFilterParams(state);
    }
    const paginationOptions = {
      ...selectors.getPaginationOptions(state),
      ...paginationParams,
    };

    if (filterOpt.name === 'matched') {
      const arr = getMatchedRulesMapIds(state);
      if (filterOpt.value === 1) {
        if (arr.length > 0) {
          filterOpt.rule__id__in = _join(arr, ',');
          filterOpt.drop = 'needs_configuration';
          filterOpt = _omit(filterOpt, ['needs_configuration']);
        } else {
          dispatch({
            type: types.SET_EMPTY_RULE_GROUPS,
            payload: {
              paginationOptions,
              filterOptions: calculateNewFilterOption(
                selectors.getFilterOptions(state),
                filterOpt,
              ),
              filterParams: {},
            },
          });
          return;
        }
      } else if (filterOpt.value === 2) {
        filterOpt.needs_configuration = true;
        filterOpt.drop = 'rule__id__in';
      } else {
        filterOpt.drop = 'all';
        filterOpt = _omit(filterOpt, ['rule__id__in', 'needs_configuration']);
      }
    }
    const dropped =
      filterOpt.drop === 'all'
        ? ['rule__id__in', 'needs_configuration']
        : [filterOpt.drop];

    const filterOptions = _omit(
      {
        ...selectors.getFilterParams(state),
        ...filterOpt,
      },
      ['matched', 'drop', ...dropped],
    );

    const finalParams = calculateFinalParams(filterOptions, paginationOptions);

    const { data } = await api.getRuleGroupsListRequest(finalParams);

    /* drop broken groups like {visible: false, rules: []}
     should be dropped when it fixed in the backend side */
    // const fixedData = dropBrokenGroups(data.results);
    const newData = mergeRulesWithGroups(data.results);
    dispatch({
      type: types.GET_RULE_GROUPS_LIST_SUCCESS,
      payload: {
        items: newData,
        count: data.count,
      },
    });

    dispatch({
      type: types.GET_RULE_GROUPS_FULL_LIST,
      payload: {
        items: data.results,
      },
    });

    dispatch({
      type: types.UPDATE_RULE_GROUPS_LIST_OPTIONS,
      payload: {
        paginationOptions,
        filterOptions: calculateNewFilterOption(
          selectors.getFilterOptions(state),
          filterOpt,
        ),
        filterParams: filterOptions,
      },
    });

    // TODO: remove this when the totalCount is provided from the back-end
    // anytime we need the total count of group that is not depends to filters
    if (!selectors.getRuleGroupsListTotalCount(state) || fetchAll) {
      const {
        data: { count: totalCount },
      } = await api.getRuleGroupsListRequest({
        size: 1,
      });
      dispatch({
        type: types.GET_RULE_GROUPS_LIST_TOTAL_COUNT,
        payload: {
          totalCount,
        },
      });
    }
  } catch (error) {
    dispatch({
      type: types.GET_RULE_GROUPS_LIST_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/** test addRuleGroup action when request is successful
 * Get rule group
 *
 * @param id
 */
export const getRuleGroup = id => async dispatch => {
  try {
    dispatch({
      type: types.GET_RULE_GROUPS_REQUEST,
    });

    const { data } = await api.getRuleGroupsRequest(id);

    dispatch({
      type: types.GET_RULE_GROUPS_SUCCESS,
      payload: {
        item: data,
      },
    });
  } catch (error) {
    dispatch({
      type: types.GET_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a rule group
 *
 * @param data
 */
export const addRuleGroup = data => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.ADD_RULE_GROUPS_REQUEST,
    });

    const response = await api.addRuleGroupsRequest(data);
    dispatch({
      type: types.ADD_RULE_GROUPS_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    dispatch({
      type: DESELECT_RULE,
    });

    const state = getState();

    if (selectors.getRuleGroup(state).id === response.data.id) {
      await dispatch(getRuleGroup(response.data.id));
    }

    await dispatch(
      getRuleGroupsList(
        {
          page: calculateCurrentPage(
            selectors.getPaginationOptions(state).size,
            response.data.order + 1,
            selectors.getPaginationOptions(state).page,
          ),
        },
        {},
        true,
      ),
    );

    Notify.success({
      title: 'Rule Group was created successfully',
    });

    return Promise.resolve({
      groupId: response.data.id,
    });
  } catch (error) {
    dispatch({
      type: types.ADD_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to add Rule Group',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/**
 * Add a rule group
 *
 * @param data
 * @param order
 */
export const addRuleGroupWithRule = (data, order) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.ADD_RULE_GROUPS_WITH_RULE_REQUEST,
    });

    const response = await api.addRuleGroupsRequest({
      name: `${data.name}_${uuid()}`,
      description: '',
      visible: false,
      rules: [data],
      order,
    });

    dispatch({
      type: types.ADD_RULE_GROUPS_WITH_RULE_SUCCESS,
    });

    const state = getState();

    await dispatch(
      getRuleGroupsList(
        {
          page: calculateCurrentPage(
            selectors.getPaginationOptions(state).size,
            order + 1,
            selectors.getPaginationOptions(state).page,
          ),
        },
        {},
        true,
      ),
    );

    await dispatch(selectRule(response.data.rules[0]));

    Notify.success({
      title: 'Rule was created successfully',
    });
    return Promise.resolve({
      groupId: response.data.id,
      id: response.data.rules[0].id,
    });
  } catch (error) {
    dispatch({
      type: types.ADD_RULE_GROUPS_WITH_RULE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Rule Group',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/* Update rule group
 *
 * @param id
 * @param data
 */
export const updateRuleGroup = (id, data) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.UPDATE_RULE_GROUPS_REQUEST,
    });
    const state = getState();
    const response = await api.updateRuleGroupsRequest(id, data);
    dispatch({
      type: types.UPDATE_RULE_GROUPS_SUCCESS,
      payload: {
        item: response.data,
      },
    });

    await dispatch(
      getRuleGroupsList({
        page: calculateCurrentPage(
          selectors.getPaginationOptions(getState()).size,
          response.data.order + 1,
          selectors.getPaginationOptions(getState()).page,
        ),
      }),
    );

    if (selectors.getRuleGroup(state).id === id) {
      await dispatch(getRuleGroup(id));
    }

    Notify.success({
      title: 'Rule Group was updated successfully',
    });
  } catch (error) {
    dispatch({
      type: types.UPDATE_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Rule Group',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Partial update rule group
 *
 * @param id
 * @param data
 * @param group
 * @param action
 */
export const partialUpdateRuleGroup = (
  id,
  data,
  group = null,
  action,
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: types.PARTIAL_UPDATE_RULE_GROUPS_REQUEST,
    });
    const state = getState();

    const response = await api.partialUpdateRuleGroupsRequest(id, data);

    dispatch({
      type: types.PARTIAL_UPDATE_RULE_GROUPS_SUCCESS,
    });

    await dispatch(
      getRuleGroupsList({
        page: selectors.getPaginationOptions(state).page,
      }),
    );

    // Update current group if it is open
    if (selectors.getRuleGroup(state).id === id) {
      await dispatch(getRuleGroup(id));
    }

    const rule = getRule(state);
    if (!_isEmpty(rule)) {
      await dispatch(getCurrentRule(rule.id, rule.rule_group));
    }

    if (action === 'move') {
      const rule = _isNull(group) ? 'Rule Group' : 'Rule';
      Notify.success({
        title: `${rule} was moved successfully`,
      });
    }

    return Promise.resolve({
      id: response.data.id,
      groupId: response.data.rule_group,
    });
  } catch (error) {
    dispatch({
      type: types.PARTIAL_UPDATE_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });

    return error;
  }
};

/**
 * Delete a rule group
 *
 * @param id
 */
export const deleteRuleGroup = (id, isVisible = false) => async (
  dispatch,
  getState,
) => {
  try {
    const state = getState();
    dispatch({
      type: types.DELETE_RULE_GROUPS_REQUEST,
    });

    await api.deleteRuleGroupRequest(id);

    dispatch({
      type: types.DELETE_RULE_GROUPS_SUCCESS,
    });

    await dispatch(
      getRuleGroupsList(
        {
          page: getCurrentPage(
            selectors.getPaginationOptions(state).page,
            selectors.getRuleGroupsList(state),
          ),
        },
        {},
        true,
      ),
    );

    Notify.success({
      title: `${isVisible ? 'Rule' : 'Rule Group'} was deleted successfully`,
    });
  } catch (error) {
    dispatch({
      type: types.DELETE_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: `Fail to delete  ${isVisible ? 'Rule' : 'Rule Group'}`,
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Reorder rule group
 *
 * @param id
 * @param data
 * @param group
 */
export const reorderRuleGroup = (id, data, group) => async (
  dispatch,
  getState,
) => {
  try {
    dispatch({
      type: types.REORDER_RULE_GROUPS_REQUEST,
    });
    const state = getState();
    await api.updateRuleGroupsRequest(id, data);

    dispatch({
      type: types.REORDER_RULE_GROUPS_SUCCESS,
    });

    await dispatch(getRuleGroupsList());

    if (selectors.getRuleGroup(state).id === id) {
      await dispatch(getRuleGroup(id));
    }
    const rule = _isNull(group) ? 'Rule Group' : 'Rule';

    Notify.success({
      title: `${rule} was reordered successfully`,
    });
  } catch (error) {
    dispatch({
      type: types.REORDER_RULE_GROUPS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Select a rule group
 *
 * @param item
 */
export const selectRuleGroup = item => async dispatch => {
  dispatch({
    type: DESELECT_RULE,
  });
  dispatch({
    type: types.SELECT_RULE_GROUPS,
    payload: {
      item,
    },
  });
};

/**
 * Deselect a rule group
 */
export const deselectRuleGroup = () => async dispatch => {
  dispatch({
    type: types.DESELECT_RULE_GROUPS,
  });
};

/**
 * Deselect a rule group and rule
 */
export const deselectRuleGroupAndRule = () => async dispatch => {
  dispatch({
    type: DESELECT_RULE,
  });
  dispatch({
    type: types.DESELECT_RULE_GROUPS,
  });
};

/**
 * Get dragged rule
 *
 * @param item
 */

export const moveRuleToList = item => async (dispatch, getState) => {
  dispatch({
    type: types.MOVE_RULE_TO_GROUP,
    payload: {
      items: dropItemFromList(selectors.getRuleGroupsList(getState()), item),
    },
  });
};
